/* CHILL theme enhancements. Shopify remains the source of catalog and checkout truth. */
(() => {
  'use strict';
  document.documentElement.classList.add('js');
  const announce = (message) => {
    const node = document.getElementById('SiteStatus');
    if (node) node.textContent = message;
  };
  if (new URL(location.href).searchParams.get('offer') === 'grand-opening') {
    const campaign = document.querySelector('[data-grand-opening-field]');
    if (campaign) {
      campaign.disabled = false;
      const note = document.querySelector('[data-grand-opening-note]');
      if (note) note.hidden = false;
    }
  }
  const openers = new WeakMap();
  function openDialog(dialog, opener) {
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;
    openers.set(dialog, opener);
    dialog.showModal();
    const focusable = dialog.querySelector('button, a[href], input, select, textarea');
    focusable?.focus();
  }
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) openDialog(document.getElementById(opener.dataset.dialogOpen), opener);
    const close = event.target.closest('[data-dialog-close]');
    if (close) close.closest('dialog')?.close();
  });
  document.addEventListener(
    'close',
    (event) => {
      if (event.target.tagName === 'DIALOG') openers.get(event.target)?.focus();
    },
    true,
  );
  document.addEventListener('click', (event) => {
    if (event.target.tagName !== 'DIALOG') return;
    const r = event.target.getBoundingClientRect();
    if (
      event.clientX < r.left ||
      event.clientX > r.right ||
      event.clientY < r.top ||
      event.clientY > r.bottom
    )
      event.target.close();
  });
  class QuantityControl extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-step]');
        if (!button || !this.input) return;
        const before = this.input.value;
        try {
          Number(button.dataset.step) > 0 ? this.input.stepUp() : this.input.stepDown();
        } catch {
          return;
        }
        if (before !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      });
      this.input?.addEventListener('input', () => this.sync());
      this.input?.addEventListener('change', () => this.sync());
      this.sync();
    }
    sync() {
      if (!this.input) return;
      const value = Number(this.input.value),
        min = Number(this.input.min || 1);
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const minus = this.querySelector('[data-step="-1"]'),
        plus = this.querySelector('[data-step="1"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
      const total = this.closest('.quantity-line')?.querySelector('[data-unit-total]');
      if (total && Number(this.dataset.pack) > 0)
        total.textContent = this.input.validity.valid ? String(value * Number(this.dataset.pack)) : '—';
      const buybox = this.closest('.product-buybox');
      const table = buybox?.querySelector('.volume-table');
      if (table && this.input.validity.valid) {
        const quantity = value + Number(table.dataset.cartQuantity || 0);
        let price = table.dataset.basePrice;
        table.querySelectorAll('[data-minimum]').forEach((row) => {
          if (quantity >= Number(row.dataset.minimum)) price = row.dataset.price;
        });
        const priceNode = buybox.querySelector('.buybox-price .price');
        if (priceNode && price) priceNode.textContent = price;
      }
    }
  }
  customElements.define('quantity-control', QuantityControl);
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[role="listbox"]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.results || !this.hasAttribute('data-enabled')) return;
      let supported = false;
      try {
        supported =
          JSON.parse(document.getElementById('shopify-features')?.textContent || '{}').predictiveSearch ===
          true;
      } catch {
        /* Full search still works. */
      }
      if (!supported) return;
      this.index = -1;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.controller?.abort();
        this.index = -1;
        this.input.removeAttribute('aria-activedescendant');
        if (this.input.value.trim().length < 2) {
          this.close();
          return;
        }
        this.close();
        this.timer = setTimeout(() => this.search(), 220);
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          clearTimeout(this.timer);
          this.controller?.abort();
          this.close();
          return;
        }
        const options = [...this.results.querySelectorAll('[role="option"]')];
        if (this.results.hidden || !options.length) return;
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
          event.preventDefault();
          this.index = (this.index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options.forEach((node, i) => node.setAttribute('aria-selected', String(i === this.index)));
          this.input.setAttribute('aria-activedescendant', options[this.index].id);
          options[this.index].scrollIntoView({ block: 'nearest' });
        } else if (event.key === 'Enter' && this.index >= 0) {
          event.preventDefault();
          options[this.index].click();
        }
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          clearTimeout(this.timer);
          this.controller?.abort();
          this.close();
        }
      });
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
    }
    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.index = -1;
    }
    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) return;
      this.controller = new AbortController();
      const controller = this.controller;
      this.status.textContent = 'Searching…';
      this.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(`${this.dataset.url}.json`, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,query');
        url.searchParams.set('resources[limit]', '5');
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Search unavailable');
        const json = await response.json();
        if (
          controller.signal.aborted ||
          this.input.value.trim() !== query ||
          !this.contains(document.activeElement)
        )
          return;
        const resources = json.resources?.results || {};
        const entries = [
          ...(resources.queries || []).map((x) => ({ ...x, kind: 'Suggested search' })),
          ...(resources.products || []).map((x) => ({ ...x, kind: 'Product' })),
          ...(resources.collections || []).map((x) => ({ ...x, kind: 'Collection' })),
        ];
        this.results.replaceChildren();
        entries.forEach((entry, i) => {
          const url = new URL(entry.url, location.origin);
          if (url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol)) return;
          const a = document.createElement('a');
          a.href = url.href;
          a.id = `${this.results.id}-option-${i}`;
          a.role = 'option';
          a.setAttribute('aria-selected', 'false');
          const type = document.createElement('span');
          type.className = 'search-result-type';
          type.textContent = entry.kind;
          const label = document.createElement('span');
          label.textContent = entry.title || entry.text || query;
          a.append(type, label);
          this.results.append(a);
        });
        const count = this.results.childElementCount;
        if (!count) {
          const p = document.createElement('p');
          p.textContent = 'No suggestions. Press Enter to search the full site.';
          this.results.append(p);
        }
        this.status.textContent = `${count} suggestions available.`;
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.close();
          this.status.textContent = 'Suggestions unavailable. Press Enter to search.';
        }
      } finally {
        if (this.controller === controller) this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.ready || !this.dataset.url) return;
      this.ready = true;
      const load = async () => {
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) return;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = doc.querySelector('product-recommendations');
          if (content?.querySelector('.product-card')) this.innerHTML = content.innerHTML;
        } catch {
          /* Optional discovery is omitted when Shopify cannot supply it. */
        }
      };
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((x) => x.isIntersecting)) {
            observer.disconnect();
            load();
          }
        },
        { rootMargin: '300px' },
      );
      observer.observe(this);
      this.observer = observer;
    }
    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);
  const filterButton = document.querySelector('[data-filter-open]');
  const panel = document.getElementById('FilterPanel');
  if (filterButton && panel && typeof HTMLDialogElement !== 'undefined') {
    const dialog = document.createElement('dialog');
    dialog.className = 'drawer filter-dialog';
    dialog.setAttribute('aria-label', 'Filter and sort products');
    const marker = document.createComment('Filter panel location');
    panel.before(marker);
    document.body.append(dialog);
    filterButton.addEventListener('click', () => {
      dialog.append(panel);
      filterButton.setAttribute('aria-expanded', 'true');
      openDialog(dialog, filterButton);
    });
    dialog.addEventListener('close', () => {
      marker.after(panel);
      filterButton.setAttribute('aria-expanded', 'false');
    });
    panel.querySelector('[data-filter-close]')?.addEventListener('click', () => dialog.close());
    window.matchMedia('(min-width: 768px)').addEventListener('change', (e) => {
      if (e.matches && dialog.open) dialog.close();
    });
  }
  // Progressive native Ajax forms: surface Shopify errors inline, then navigate/reload on success.
  function enhanceCommerceForm(form, mode) {
    if (!form || form.hasAttribute('data-preview-form')) return;
    form.addEventListener('submit', async (event) => {
      if (mode === 'update' && event.submitter?.name === 'checkout') return;
      const endpoint = mode === 'add' ? form.action : form.dataset.updateUrl;
      if (!endpoint) return;
      event.preventDefault();
      if (form.dataset.submitting || !form.reportValidity()) return;
      const url = new URL(endpoint, location.origin);
      if (url.origin !== location.origin) return;
      url.pathname = url.pathname.replace(/\.js$/, '') + '.js';
      const button = form.querySelector(mode === 'add' ? '[name="add"]' : '[name="update"]');
      if (!button || button.disabled) return;
      const original = button.innerHTML;
      const error = form.querySelector('[data-commerce-error]') || document.createElement('p');
      error.dataset.commerceError = '';
      error.className = 'notice error';
      error.role = 'alert';
      error.tabIndex = -1;
      error.hidden = true;
      if (!error.parentNode) button.before(error);
      form.dataset.submitting = 'true';
      button.disabled = true;
      button.textContent = mode === 'add' ? 'Adding…' : 'Updating…';
      try {
        let body = new FormData(form),
          headers = { Accept: 'application/json' };
        if (mode === 'update') {
          const updates = {};
          form.querySelectorAll('[data-line-key]').forEach((input) => {
            updates[input.dataset.lineKey] = Number(input.value);
          });
          body = JSON.stringify({ updates, note: form.elements.note?.value || '' });
          headers['Content-Type'] = 'application/json';
        }
        const response = await fetch(url, { method: 'POST', headers, body });
        const data = await response.json();
        if (!response.ok || data.status >= 400) {
          const detail = data.description || data.message || data.errors;
          error.textContent =
            typeof detail === 'string'
              ? detail
              : 'Shopify could not accept this quantity. Review the order conditions and try again.';
          error.hidden = false;
          error.focus();
          return;
        }
        if (mode === 'add' && form.dataset.cartUrl) location.assign(form.dataset.cartUrl);
        else location.reload();
      } catch {
        error.textContent =
          'We could not confirm the cart update. Review your cart before trying again to avoid adding items twice.';
        error.hidden = false;
        error.focus();
      } finally {
        delete form.dataset.submitting;
        button.disabled = false;
        button.innerHTML = original;
      }
    });
  }
  document.querySelectorAll('form.product-form').forEach((form) => enhanceCommerceForm(form, 'add'));
  const cart = document.getElementById('CartForm');
  enhanceCommerceForm(cart, 'update');
  cart?.addEventListener('change', (event) => {
    if (event.target.name !== 'updates[]') return;
    const checkout = cart.querySelector('[name="checkout"]');
    if (checkout) {
      checkout.disabled = true;
      checkout.title = 'Update the cart to review the recalculated totals first.';
    }
    announce('Quantity changed. Update the cart to review totals before checkout.');
  });
  document.querySelector('.notice[role="status"][tabindex],.notice[role="alert"][tabindex]')?.focus();
})();

// Each Shopify section instance owns its one-time decorative reveal and cleanup.
if (!customElements.get('chill-process')) {
  customElements.define('chill-process', class extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.classList.add('is-revealed');
        this.observer.disconnect();
      }, { threshold: 0.15 });
      this.observer.observe(this);
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  });
}
